/* eslint-disable sonarjs/no-duplicate-string */
import { useState } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import { EUFlagIcon } from './icons/EUFlagIcon';
import { USFlagIcon } from './icons/USFlagIcon';
import { Alert } from './common/alerts/Alert';
import { Badge } from './common/Badge';
import { BrandHeader } from './BrandHeader';
import { Button } from './common/buttons/Button';
import { InformationChip } from './common/chips/InformationChip';
import { RadioGroup } from './common/radio/RadioGroup';
import { StyledPageInnerContainer, StyledPageOuterContainer } from './layout/PageComponents';
import {
    StyledBorderBox,
    StyledBorderBoxContent,
    StyledBorderBoxHeader,
    StyledBorderBoxHeaderColumn,
    StyledBorderBoxHeaderRow,
} from './layout/BorderBoxComponents';

type Realm = 'eu' | 'us';

export interface RealmSelectorProps {
    euAppUrl: string;
    queryParams?: string;
    urlPath?: string;
    usAppUrl: string;
}

const StyledLabel = styled(Box)(({ theme }) => ({
    ...theme.typography.flexAlignCenter,
    gap: theme.spacing(2),
    '& .MuiSvgIcon-root': {
        height: 24,
        width: 24,
    },
}));

export const RealmSelector: React.FC<RealmSelectorProps> = ({ euAppUrl, queryParams = '', urlPath = '', usAppUrl }) => {
    const [realm, setRealm] = useState<Realm>('eu');

    const realmUrl = realm === 'us' ? usAppUrl : euAppUrl;
    const redirectionUrl = `${realmUrl}${urlPath}${queryParams}`;

    return (
        <>
            <BrandHeader />
            <StyledPageOuterContainer>
                <StyledPageInnerContainer maxWidth={752}>
                    <StyledBorderBox>
                        <StyledBorderBoxHeader>
                            <StyledBorderBoxHeaderColumn>
                                <StyledBorderBoxHeaderRow>
                                    <Typography variant="h6">Which region do you want to access?</Typography>
                                    <Badge icon={<PublicOutlinedIcon />} size="small" />
                                </StyledBorderBoxHeaderRow>
                                <Typography color="text.secondary" fontWeight="normal" variant="subtitle1">
                                    Consider business location, customers and compliance (e.g. GDPR) when choosing.
                                </Typography>
                            </StyledBorderBoxHeaderColumn>
                        </StyledBorderBoxHeader>
                        <Divider />
                        <StyledBorderBoxContent>
                            <Alert
                                severity="info"
                                title="Not sure where your account is located? It is likely to be in the EU region."
                            />
                            <RadioGroup
                                buttons={[
                                    {
                                        endIcon: <InformationChip label="Default" severity="grey" />,
                                        label: (
                                            <StyledLabel>
                                                <EUFlagIcon />
                                                <Typography>European Union</Typography>
                                            </StyledLabel>
                                        ),
                                        value: 'eu',
                                    },
                                    {
                                        endIcon: <InformationChip label="New" severity="grey" />,
                                        label: (
                                            <StyledLabel>
                                                <USFlagIcon />
                                                <Typography>United States</Typography>
                                            </StyledLabel>
                                        ),
                                        value: 'us',
                                    },
                                ]}
                                layout="list"
                                size="medium"
                                value={realm}
                                onChange={(value) => setRealm(value as Realm)}
                            />
                        </StyledBorderBoxContent>
                    </StyledBorderBox>
                    <Button link={{ href: redirectionUrl, type: 'external' }}>{`Continue with ${
                        realm === 'eu' ? 'European Union' : 'United States'
                    }`}</Button>
                </StyledPageInnerContainer>
            </StyledPageOuterContainer>
        </>
    );
};
